import {
  type ShadeDay,
  type ShadeMap,
  type ShadeUnit,
  buildShadeMap,
  shadeAlpha,
  shadeLevel,
  shadeSummary,
  shadeTip,
} from '@goaltree/backend/convex/lib/dotIntensity'
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import type { CellMark } from '@/components/habit-dots'

export type { ShadeDay, ShadeMap }
export { shadeSummary, shadeTip }
type Measure = ShadeUnit
type ShadeSource = {
  shade?: boolean
  measure?: Measure | null
  dayAmounts?: readonly { date: string; amount: number; target: number }[]
}

const OVER_RING = 'inset 0 0 0 1.5px color-mix(in srgb, var(--foreground) 92%, transparent)'

/** Per-day shade for a habit that shades by amount; null when it doesn't. */
export function useShadeMap(src: ShadeSource | undefined | null): ShadeMap | null {
  const on = !!src?.shade && !!src.measure
  const direction = src?.measure?.direction
  const amounts = src?.dayAmounts
  return useMemo(
    () => (on && direction ? buildShadeMap(amounts, direction) : null),
    [on, direction, amounts],
  )
}

/**
 * A cell's intensity on top of its style: the glyph or fill is dimmed by the
 * day's credit (filter opacity, so a finish drawn by `base` dims with it and
 * the grid's own fade-in is untouched), and a day over target gets a ring.
 */
export function shadeMark(base: CellMark | null, s: ShadeDay | undefined): CellMark | null {
  if (!s || s.credit <= 0) return base
  const a = shadeAlpha(s.credit)
  const style: CSSProperties = { backgroundColor: 'var(--habit)', ...base?.style }
  if (a < 1) style.filter = `opacity(${a.toFixed(2)})`
  if (s.over) style.boxShadow = OVER_RING
  return { style, children: base?.children ?? null }
}

/**
 * Tap or hover a cell: a small bubble over it. Cells carry `data-day` and
 * `data-tip`; `onDay` may claim the press first (e.g. open a photo).
 */
export function useCellTip(onDay?: (ds: string) => boolean) {
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])
  const press = useCallback(
    (e: React.MouseEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-day]')
      if (!el) return
      if (onDay?.(el.dataset.day!)) {
        e.stopPropagation()
        return
      }
      const text = el.dataset.tip
      if (!text) return
      e.stopPropagation()
      const r = el.getBoundingClientRect()
      setTip({ x: r.left + r.width / 2, y: r.top, text })
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setTip(null), 2400)
    },
    [onDay],
  )
  const bubble =
    tip && typeof document !== 'undefined'
      ? createPortal(
          <div
            role="status"
            className="pointer-events-none fixed z-[80] -translate-x-1/2 -translate-y-full rounded-lg bg-foreground px-2.5 py-1.5 text-[11px] font-semibold whitespace-nowrap text-background shadow-lg"
            style={{
              left: Math.min(Math.max(tip.x, 80), window.innerWidth - 80),
              top: tip.y - 6,
            }}
          >
            {tip.text}
          </div>,
          document.body,
        )
      : null
  return { press, bubble }
}

/** "Less ▢▢▢▢▢ More" (+ the over-target ring), in the habit's colour. */
export function ShadeLegend({ over = true }: { over?: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground/80">
      <span>{t('dotShade.less')}</span>
      {[0.2, 0.4, 0.6, 0.8, 1].map((c) => (
        <span
          key={c}
          data-level={shadeLevel(c)}
          className="size-3 rounded-[3px] bg-[var(--habit)]"
          style={{ filter: `opacity(${shadeAlpha(c).toFixed(2)})` }}
        />
      ))}
      <span>{t('dotShade.more')}</span>
      {over && (
        <span className="ml-2 flex items-center gap-1">
          <span
            className="size-3 rounded-[3px] bg-[var(--habit)]"
            style={{ boxShadow: OVER_RING }}
          />
          {t('dotShade.over')}
        </span>
      )}
    </div>
  )
}
